import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcFundingV1BatchTransferQueryServiceBatchTransferGet } from '@interxfi/api-client/generated/hooks';
import { TenantContext, useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcFundingV1BatchTransferState } from '@interxfi/types';
import { FundingBatchAllocationsTable } from '../../../../../../features/funding/batch-transfer/allocation/funding-batch-allocations-table';
import { FundingBatchTransferDirectionPill } from '../../../../../../features/funding/batch-transfer/funding-batch-transfer-direction-pill';
import { FundingBatchTransferEntityMenu } from '../../../../../../features/funding/batch-transfer/funding-batch-transfer-entity-menu';
import { FundingBatchTransferEventSummary } from '../../../../../../features/funding/batch-transfer/funding-batch-transfer-event';
import { getFundingBatchTransferStateListItems } from '../../../../../../features/funding/batch-transfer/funding-batch-transfer-list-items';
import { getFundingBatchTransferDetailPath } from '../../../../../../features/funding/batch-transfer/funding-batch-transfer-routing';
import { FundingBatchTransferStatusBanner } from '../../../../../../features/funding/batch-transfer/funding-batch-transfer-status-banner';
import { FundingBatchTransferStatusPill } from '../../../../../../features/funding/batch-transfer/funding-batch-transfer-status-pill';
import { FundingBatchTransferSummaryBar } from '../../../../../../features/funding/batch-transfer/funding-batch-transfer-summary-bar';
import { ProgramLogomark } from '../../../../../../features/userauth/program/logomark/program-logomark';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/funding/batch-transfer/_batch-transfer/$batchTransferId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `funding-batch-transfer-${params.batchTransferId}`,
        label: 'Batch Transfer',
        to: getFundingBatchTransferDetailPath(params.batchTransferId),
        suffix: (
          <CopyButton
            content={params.batchTransferId}
            label="Copy Batch Transfer ID"
            tooltip={{ copy: params.batchTransferId, copied: 'Copied Batch Transfer ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Batch Transfer'),
  component: FundingBatchTransferDetail,
});

function getListItems(data: IxcFundingV1BatchTransferState | undefined) {
  const items = getFundingBatchTransferStateListItems(data);
  const typeDetailHeader = match(items.data.spec.batchTransferType)
    .with(P.union({ achPush: P.not(P.nullish) }, { achPull: P.not(P.nullish) }), () => 'ACH Details')
    .otherwise(() => undefined);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [
            items.data.spec.totalAmount,
            items.data.spec.allocationCount,
            items.data.platformAccountId,
            ...(items.data.externalId ? [items.data.externalId] : []),
          ],
        },
        ...(typeDetailHeader
          ? [
              {
                id: 'typeDetails',
                title: typeDetailHeader,
                items: [
                  items.data.spec.batchTransferType.typeNamePillWithoutPrefix,
                  ...('achPull' in items.data.spec.batchTransferType
                    ? [
                        items.data.spec.batchTransferType.achPull.value.isSameDayRequested,
                        items.data.spec.batchTransferType.achPull.value.debitAuthorization,
                      ]
                    : []),
                  ...('achPush' in items.data.spec.batchTransferType ? [items.data.spec.batchTransferType.achPush.value.isSameDayRequested] : []),
                ],
              },
            ]
          : []),
        ...(items.data.counts && Object.keys(items.data.counts).length > 0
          ? [
              {
                id: 'counts',
                title: 'Counts',
                items: [
                  items.data.counts.createdInternal,
                  items.data.counts.createdExternal,
                  items.data.counts.errorInternal,
                  items.data.counts.errorExternal,
                  items.data.counts.successInternal,
                  items.data.counts.successExternal,
                  items.data.counts.totalInternal,
                  items.data.counts.totalExternal,
                ],
              },
            ]
          : []),
      ] as const satisfies ReadonlyListGroup[],
      right: [
        items.metadata.createdAt,
        items.metadata.updatedAt,
        items.batchTransferId,
        ...(items.data.transferId ? [items.data.transferId] : []),
        ...(items.data.spec.sourceId ? [items.data.spec.sourceId] : []),
        items.programId,
      ] as const satisfies ReadonlyListItem[],
    },
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load batch transfer', 'view this batch transfer');

function FundingBatchTransferDetail() {
  const { batchTransferId } = Route.useParams();
  const request = { batchTransferId } as const;
  const { data, error, isPending } = useIxcFundingV1BatchTransferQueryServiceBatchTransferGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.batchTransfer), [data?.batchTransfer]);
  const { activeTenantContext } = useActiveRealmAPIContext();

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <VerticalStack gap={7}>
          <FundingBatchTransferStatusBanner batchTransferId={batchTransferId} />

          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              {activeTenantContext !== TenantContext.Program ? (
                <ProgramLogomark programId={data?.batchTransfer?.programId} shouldLoadLogoMark size="large" withTooltip />
              ) : null}

              <FundingBatchTransferStatusPill isPending={showPending} size="large" status={data?.batchTransfer?.status} />
              <FundingBatchTransferDirectionPill isPending={showPending} size="large" type={data?.batchTransfer?.data?.spec?.batchTransferType} />
            </HorizontalStack>

            <FundingBatchTransferEntityMenu batchTransferId={batchTransferId} />
          </PageHeader>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <FundingBatchTransferSummaryBar batchTransferId={batchTransferId} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <FundingBatchTransferEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={7}>
          <Typography.Level3>Allocations</Typography.Level3>

          <FundingBatchAllocationsTable view={{ kind: 'batchTransfer', batchTransferId }} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
